import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AppDispatch, RootState } from '../store'
import { canRedirectToLogin } from '../lib/apiFetch'
import { login, postLoginReturnTo } from '../components/UserMenu'
import { useEmbedded } from '../lib/embed'
import { isUnauthorizedError } from '../lib/unauthorizedError'

/**
 * Sends a signed-out visitor to the sign-in screen when the content they opened turns out to be
 * private, returning them to the same URL afterwards (and signing them in without a prompt when
 * Auth0 still has a session). Pass the query that fetches the page's own entity — a 401 on it means
 * the whole page is unreadable, whereas a 401 on a member-only side request (project members,
 * subscription, notifications) is normal for a signed-out visitor of a PUBLIC project and must not
 * interrupt them.
 *
 * Returns true while the redirect is being set up, so the caller renders its loading state instead of
 * an error the visitor can do nothing with. It returns false — leaving the caller's error state with
 * its Sign in button as the fallback — when the redirect can't be used: inside the embedded iframe
 * (which can't host the login provider; those surfaces offer a popup login instead), or once the
 * redirect budget is spent, which is what stops a login that keeps coming back unauthenticated from
 * looping.
 */
export function useSignInOnUnauthorized(error: unknown): boolean {
  const dispatch = useDispatch<AppDispatch>()
  const embedded = useEmbedded()
  const { user, checked, authError } = useSelector((state: RootState) => state.auth)
  const [throttled, setThrottled] = useState(false)
  // `checked` (and no authError) means /api/auth-me actually reported no user, so this is a signed-out
  // visitor rather than a session we haven't looked at yet.
  const redirecting = isUnauthorizedError(error) && checked && !user && !authError && !embedded && !throttled

  useEffect(() => {
    if (!redirecting) return
    if (!canRedirectToLogin()) {
      setThrottled(true)
      return
    }
    login(dispatch, postLoginReturnTo(window.location.pathname, window.location.search))
  }, [dispatch, redirecting])

  return redirecting
}
